import React, { useState, useEffect } from 'react';
import { Anchor, Users, TrendingUp, DollarSign, Clock, CheckCircle, AlertTriangle, Search, Download, Plus, Mail, Phone, MessageSquare, Star, Calendar, FileText, Settings, Zap, Send, CreditCard, MapPin, Eye, Edit, Filter, BarChart3, Shield } from 'lucide-react';

const MybaWorkflow = () => {
  const [selectedStage, setSelectedStage] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedCharter, setSelectedCharter] = useState(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);

  // Statistiques du pipeline
  const stats = {
    activeCharters: 47,
    pipelineValue: "€18.7M",
    avgResponseTime: "3.2 jours",
    automationRate: 92,
    contractCompliance: 87,
    clientSatisfaction: 4.9
  };

  // Pipelines du workflow MYBA
  const workflowStages = [
    {
      id: 'enquiry',
      name: 'Enquiry',
      icon: '🔍',
      count: 2,
      color: 'blue'
    },
    {
      id: 'option',
      name: 'Option',
      icon: '⏳',
      count: 1,
      color: 'yellow'
    },
    {
      id: 'contract',
      name: 'Contract',
      icon: '📝',
      count: 1,
      color: 'orange'
    },
    {
      id: 'payment',
      name: 'Payment',
      icon: '💰',
      count: 1,
      color: 'purple'
    },
    {
      id: 'preparation',
      name: 'Preparation',
      icon: '🛠️',
      count: 1,
      color: 'teal'
    },
    {
      id: 'active',
      name: 'Active',
      icon: '⛵',
      count: 1,
      color: 'green'
    },
    {
      id: 'closing',
      name: 'Closing',
      icon: '✅',
      count: 1,
      color: 'gray'
    }
  ];

  // Charters par étape
  const chartersByStage = {
    enquiry: [
      {
        id: 'AZIMUT-85',
        yacht: 'M/Y AZIMUT 85',
        client: 'James Chen',
        dates: '15-29 Aug 2025',
        value: '€285,000',
        status: 'VIP',
        location: 'Med',
        priority: 'high'
      },
      {
        id: 'PERINI-56',
        yacht: 'S/Y PERINI NAVI 56m',
        client: 'Al-Rashid Family',
        dates: '1-14 Sep 2025',
        value: '€420,000',
        status: 'Urgent',
        location: 'Med',
        priority: 'urgent'
      }
    ],
    option: [
      {
        id: 'BENETTI-63',
        yacht: 'M/Y BENETTI 63m',
        client: 'Sophie Laurent',
        dates: '20 Jul - 3 Aug',
        value: '€580,000',
        status: '48h hold',
        location: 'Med',
        priority: 'medium'
      }
    ],
    contract: [
      {
        id: 'LURSSEN-75',
        yacht: 'M/Y LURSSEN 75m',
        client: 'Vladimir Petrov',
        dates: '5-19 Aug 2025',
        value: '€950,000',
        status: 'VIP',
        location: 'Signing',
        priority: 'high'
      }
    ],
    payment: [
      {
        id: 'AMELS-60',
        yacht: 'M/Y AMELS 60m',
        client: 'Tech Corp Event',
        dates: '10-17 Jul 2025',
        value: '€520,000',
        status: 'APA Due',
        location: 'Payment',
        priority: 'medium'
      }
    ],
    preparation: [
      {
        id: 'FEADSHIP-70',
        yacht: 'M/Y FEADSHIP 70m',
        client: 'Royal Family',
        dates: '1-21 Jul 2025',
        value: '€1,200,000',
        status: 'VVIP',
        location: 'Provisioning',
        priority: 'high'
      }
    ],
    active: [
      {
        id: 'OCEANCO-90',
        yacht: 'M/Y OCEANCO 90m',
        client: 'Williams Group',
        dates: 'Current: Day 4/14',
        value: '€1,800,000',
        status: 'Cruising',
        location: 'Monaco',
        priority: 'active'
      }
    ],
    closing: [
      {
        id: 'SUNSEEKER-40',
        yacht: 'M/Y SUNSEEKER 40m',
        client: 'Johnson Family',
        dates: 'Completed',
        value: '€180,000',
        status: 'APA Reconcile',
        location: 'Closing',
        priority: 'low'
      }
    ]
  };

  // Activités récentes
  const recentActivities = [
    {
      time: 'Il y a 10 minutes',
      action: 'Contract Signed - M/Y LURSSEN 75m',
      details: 'Vladimir Petrov signed MYBA agreement via DocuSign'
    },
    {
      time: 'Il y a 2 heures',
      action: 'Deposit Received - M/Y FEADSHIP 70m',
      details: '50% deposit (€600,000) received via wire transfer'
    },
    {
      time: 'Il y a 3 heures',
      action: 'APA Reminder Sent - M/Y AMELS 60m',
      details: 'Automatic reminder for APA payment (30% - €156,000)'
    },
    {
      time: 'Il y a 5 heures',
      action: 'Preference Form Completed - S/Y PERINI NAVI',
      details: 'Guest preferences received and forwarded to captain'
    }
  ];

  // Règles d'automatisation
  const automationRules = [
    {
      trigger: 'When contract is signed',
      action: 'Send deposit invoice (50%) + Start 48h countdown',
      icon: '📝'
    },
    {
      trigger: 'When deposit received',
      action: 'Send preference form + Notify captain + Update central agent',
      icon: '💳'
    },
    {
      trigger: '45 days before charter',
      action: 'Send APA request (30%) + Balance reminder',
      icon: '📅'
    },
    {
      trigger: '48h before embarkation',
      action: 'Send embarkation details + Captain introduction + WiFi codes',
      icon: '⏰'
    },
    {
      trigger: 'On disembarkation',
      action: 'Generate APA statement + Request feedback + Offer rebooking',
      icon: '✅'
    }
  ];

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'vip': return 'bg-purple-100 text-purple-800';
      case 'vvip': return 'bg-purple-200 text-purple-900';
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'cruising': return 'bg-blue-100 text-blue-800';
      case '48h hold': return 'bg-yellow-100 text-yellow-800';
      case 'apa due': return 'bg-orange-100 text-orange-800';
      case 'signing': return 'bg-green-100 text-green-800';
      case 'provisioning': return 'bg-teal-100 text-teal-800';
      case 'apa reconcile': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200',
      yellow: 'bg-yellow-50 border-yellow-200',
      orange: 'bg-orange-50 border-orange-200',
      purple: 'bg-purple-50 border-purple-200',
      teal: 'bg-teal-50 border-teal-200',
      green: 'bg-green-50 border-green-200',
      gray: 'bg-gray-50 border-gray-200'
    };
    return colors[color] || 'bg-gray-50 border-gray-200';
  };

  const CharterCard = ({ charter, stageId }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-800 text-sm">{charter.yacht}</h4>
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(charter.status)}`}>
          {charter.status}
        </span>
      </div>
      
      <p className="text-sm text-gray-600 mb-2">{charter.client}</p>
      <p className="text-xs text-gray-500 mb-2">{charter.dates}</p>
      
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-green-600">{charter.value}</span>
        <span className="text-xs text-gray-500">{charter.location}</span>
      </div>
    </div>
  );

  return (
    <div className="myba-workflow">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center space-x-3">
            <Anchor className="text-blue-600" size={32} />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">MYBA Charter Workflow Pipeline</h1>
              <p className="text-gray-600 mt-1">⚓ Mediterranean Standard - Processus automatisé de gestion des charters</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Filter size={18} />
              <span>Filtres</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Download size={18} />
              <span>Export</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouveau Charter</span>
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-blue-600" size={24} />
              <span className="text-sm font-medium text-blue-600">↑ 12%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.activeCharters}</div>
            <div className="text-sm text-gray-600">Active Charters</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-green-600" size={24} />
              <span className="text-sm font-medium text-green-600">↑ 23% YoY</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.pipelineValue}</div>
            <div className="text-sm text-gray-600">Pipeline Value</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-yellow-600" size={24} />
              <span className="text-sm font-medium text-yellow-600">↓ 18%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.avgResponseTime}</div>
            <div className="text-sm text-gray-600">Avg. Response Time</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Zap className="text-purple-600" size={24} />
              <span className="text-sm font-medium text-purple-600">↑ 5%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.automationRate}%</div>
            <div className="text-sm text-gray-600">Automation Rate</div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-teal-600" size={24} />
              <span className="text-sm font-medium text-teal-600">MYBA</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.contractCompliance}%</div>
            <div className="text-sm text-gray-600">Contract Compliance</div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Star className="text-orange-600" size={24} />
              <span className="text-sm font-medium text-orange-600">↑ 0.2</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.clientSatisfaction}/5</div>
            <div className="text-sm text-gray-600">Client Satisfaction</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <select 
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">All Charters</option>
              <option value="motor">Motor Yachts</option>
              <option value="sailing">Sailing</option>
              <option value="this-week">This Week</option>
              <option value="vip">VIP Only</option>
            </select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher un charter..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
          </div>
        </div>

        {/* Pipeline Kanban */}
        <div className="pipeline-container mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Charter Pipeline Status</h3>
          <div className="flex space-x-6 overflow-x-auto pb-6">
            {workflowStages.map((stage) => (
              <div key={stage.id} className={`min-w-80 ${getStageColor(stage.color)} border rounded-lg p-4`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{stage.icon}</span>
                    <h4 className="font-semibold text-gray-800">{stage.name}</h4>
                  </div>
                  <span className="text-sm font-medium text-gray-600">{stage.count}</span>
                </div>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {(chartersByStage[stage.id] || []).map((charter) => (
                    <CharterCard key={charter.id} charter={charter} stageId={stage.id} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Actions Rapides */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: FileText, label: 'Send MYBA Contract', color: 'blue' },
                { icon: CreditCard, label: 'Request Deposit', color: 'green' },
                { icon: BarChart3, label: 'Calculate APA', color: 'purple' },
                { icon: FileText, label: 'Preference Form', color: 'orange' },
                { icon: Users, label: 'Notify Captain', color: 'teal' },
                { icon: CheckCircle, label: 'Clearance Check', color: 'yellow' },
                { icon: Settings, label: 'Provisioning List', color: 'indigo' },
                { icon: Shield, label: 'Insurance Verify', color: 'red' }
              ].map((action, index) => (
                <button key={index} className={`flex items-center space-x-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 text-${action.color}-600 border-${action.color}-300`}>
                  <action.icon size={16} />
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Activités Récentes */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Recent Activities</h3>
              <button className="text-sm text-blue-600 hover:text-blue-700">Filter</button>
            </div>
            
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {recentActivities.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">{activity.action}</p>
                    <p className="text-xs text-gray-600">{activity.details}</p>
                    <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Règles d'Automatisation */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Active Automation Rules</h3>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Plus size={16} />
              <span>Add Rule</span>
            </button>
          </div>
          
          <div className="space-y-4">
            {automationRules.map((rule, index) => (
              <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                <span className="text-lg">{rule.icon}</span>
                <div className="flex-1">
                  <span className="font-medium text-gray-800">{rule.trigger}</span>
                  <span className="mx-2 text-gray-400">→</span>
                  <span className="text-gray-600">{rule.action}</span>
                </div>
                <button className="text-gray-400 hover:text-gray-600">
                  <Edit size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MybaWorkflow;